import React from "react";
import { Star, Award, TrendingUp, Target } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "center") return "text-center";
  if (v === "right") return "text-right";
  if (v === "left") return "text-left";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getIconByIndex = (index) => {
  const icons = [
    <TrendingUp className="w-6 h-6" />,
    <Award className="w-6 h-6" />,
    <Target className="w-6 h-6" />,
  ];
  return icons[index % icons.length];
};

const TestimonialsSection3 = ({ config, style }) => {
  const testimonials = Array.isArray(config?.testimonials) ? config.testimonials : [];

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;

  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const cardAlignClass = hasAlign ? toAlignClass(align) : "";

  const iconAlignClass =
    !hasAlign || align === "center"
      ? "mx-auto"
      : align === "right"
      ? "ms-auto"
      : "me-auto";

  const renderStars = (count = 5) => {
    return Array.from({ length: count }).map((_, i) => (
      <Star
        key={i}
        style={{
          color: style?.testimonialsStarActiveColor || undefined,
          fill: style?.testimonialsStarActiveColor || undefined,
        }}
        className="w-5 h-5 text-yellow-400 fill-yellow-400"
      />
    ));
  };

  const bg = buildBg(style, "bg-white");

  if (!testimonials.length) return null;

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-14 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`${headerAlignClass} mb-20`}>
          <div
            style={{ backgroundColor: style?.testimonialsTopIconBg || undefined }}
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 mb-6 ${iconAlignClass}`}
          >
            <Star
              style={{ color: style?.testimonialsTopIconColor || undefined }}
              className="w-10 h-10 text-blue-600"
            />
          </div>

          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {config?.title || "Proven Results"}
          </h2>

          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config?.subtitle || "Real businesses achieving real results with SmartBusiness"}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="relative group">
              <div
                style={{
                  backgroundColor: style?.testimonialsCardBg || undefined,
                  borderColor: style?.testimonialsCardBorder || undefined,
                }}
                className={`rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border h-full ${cardAlignClass}`}
              >
                <div
                  style={{
                    backgroundColor: style?.testimonialsAccentBg || undefined,
                    color: style?.testimonialsAccentText || undefined,
                  }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-6"
                >
                  {getIconByIndex(index)}
                </div>

                <div className="flex mb-6">{renderStars()}</div>

                <p
                  style={{ color: style?.testimonialsContentColor || undefined }}
                  className="text-2xl text-gray-800 mb-8 font-medium"
                >
                  "{testimonial.content}"
                </p>

                <div className="border-t border-gray-200 pt-6">
                  <h4
                    style={{ color: style?.testimonialsNameColor || undefined }}
                    className="text-lg font-bold text-gray-900"
                  >
                    {testimonial.name}
                  </h4>
                  <p
                    style={{ color: style?.testimonialsRoleColor || undefined }}
                    className="text-gray-600 mb-4"
                  >
                    {testimonial.role}
                  </p>
                  <div
                    style={{
                      backgroundColor: style?.testimonialsMetricBadgeBg || undefined,
                      color: style?.testimonialsMetricBadgeText || undefined,
                    }}
                    className="inline-block px-4 py-2 rounded-full font-semibold"
                  >
                    {testimonial.metrics}
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: style?.testimonialsResultBadgeBg || undefined,
                  color: style?.testimonialsResultBadgeText || undefined,
                }}
                className="absolute -top-4 -end-4 bg-white rounded-2xl p-4 shadow-lg"
              >
                <div className="text-3xl font-bold">#{index + 1}</div>
                <div className="text-xs opacity-80">Top Result</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection3;